import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from "lucide-react";

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, updateQty, subtotal } = useCart();
  const navigate = useNavigate();

  const checkout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-md bg-[color:var(--dms-cream)] border-l border-[color:var(--dms-navy)]/10 flex flex-col">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl text-[color:var(--dms-navy)] flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" /> Your cart
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto mt-6 -mx-6 px-6">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-14 h-14 rounded-full bg-[color:var(--dms-peach-light)]/50 mx-auto flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-[color:var(--dms-navy)]" />
              </div>
              <p className="mt-4 font-serif text-xl text-[color:var(--dms-navy)]">Your cart is empty</p>
              <p className="mt-2 text-sm text-[color:var(--dms-navy)]/60">Add a shirt and we'll show it here.</p>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li key={item.key} className="flex gap-4 bg-white rounded-xl border border-[color:var(--dms-navy)]/10 p-3">
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-[color:var(--dms-peach-light)]/40 flex-shrink-0">
                    <img src={item.img} alt={item.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-[color:var(--dms-navy)] text-sm leading-snug line-clamp-2">{item.title}</p>
                    <p className="text-xs text-[color:var(--dms-navy)]/60 mt-1">
                      <span className="inline-block w-3 h-3 rounded-full mr-1 align-middle border border-black/10" style={{ background: item.color }} />
                      {item.colorName} · Size {item.size}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-[color:var(--dms-navy)]/15 rounded-full">
                        <button onClick={() => updateQty(item.key, item.qty - 1)} className="w-7 h-7 flex items-center justify-center text-[color:var(--dms-navy)] hover:bg-[color:var(--dms-navy)]/5"><Minus className="w-3 h-3" /></button>
                        <span className="px-2 text-sm text-[color:var(--dms-navy)] min-w-[28px] text-center">{item.qty}</span>
                        <button onClick={() => updateQty(item.key, item.qty + 1)} className="w-7 h-7 flex items-center justify-center text-[color:var(--dms-navy)] hover:bg-[color:var(--dms-navy)]/5"><Plus className="w-3 h-3" /></button>
                      </div>
                      <p className="font-serif text-[color:var(--dms-navy)]">{item.price}</p>
                    </div>
                  </div>
                  <button onClick={() => removeItem(item.key)} className="text-[color:var(--dms-navy)]/40 hover:text-red-600 transition-colors self-start" aria-label="Remove">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-[color:var(--dms-navy)]/10 pt-5 mt-4">
            <div className="flex items-center justify-between text-[color:var(--dms-navy)]">
              <span className="text-sm tracking-widest uppercase font-semibold">Subtotal</span>
              <span className="font-serif text-2xl">${subtotal.toFixed(2)}</span>
            </div>
            <p className="text-xs text-[color:var(--dms-navy)]/55 mt-1">Taxes calculated at checkout. Free shipping on every order.</p>
            <button onClick={checkout} className="btn-peach mt-5 w-full justify-center">Checkout <ArrowRight className="w-4 h-4" /></button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
